import React, { useState } from 'react';
import { Camera, Loader2, CheckCircle2, XCircle, ImageIcon } from 'lucide-react';
import { uploadApontamentoFoto } from './kioskUtils';

function paraNumero(v) {
  if (v == null || v === '') return null;
  const n = parseFloat(String(v).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Calcula conformidade de um parâmetro de faixa (range). */
export function conformeRange(param, valor) {
  if (param.tipo !== 'range') return null;
  const n = paraNumero(valor);
  if (n == null) return null;
  const min = param.min != null ? Number(param.min) : null;
  const max = param.max != null ? Number(param.max) : null;
  if (min != null && n < min) return false;
  if (max != null && n > max) return false;
  return true;
}

/** Monta o array de parâmetros para o apontamento (qualidade + produção). */
export function construirParametrosPayload(etapa, qualidadeVals, producaoVals) {
  const out = [];
  (etapa?.parametros || []).forEach((p) => {
    const valor = qualidadeVals[p.nome] ?? '';
    out.push({
      categoria: 'qualidade',
      nome: p.nome,
      tipo: p.tipo || 'texto',
      unidade: p.unidade || '',
      min: p.min ?? null,
      max: p.max ?? null,
      valor,
      conforme: conformeRange(p, valor),
    });
  });
  (etapa?.validacoes || []).forEach((v) => {
    out.push({
      categoria: 'producao',
      nome: v.nome,
      valor: producaoVals[v.nome] ?? '',
    });
  });
  return out;
}

/** Lista nomes dos parâmetros obrigatórios ainda não preenchidos. */
export function obrigatoriosFaltando(etapa, qualidadeVals, pesoFinal = '') {
  const faltam = [];
  (etapa?.parametros || []).forEach((p) => {
    if (p.obrigatorio && String(qualidadeVals[p.nome] ?? '').trim() === '') faltam.push(p.nome);
  });
  if (etapa?.peso_final_ativo) {
    const n = paraNumero(pesoFinal);
    if (n == null || n <= 0) faltam.push('Peso Final');
  }
  return faltam;
}

/** Campo de peso final (quando a etapa tem captura ativada no produto). */
export function PesoFinalEtapaField({ etapa, valor, setValor, unidade = 'kg' }) {
  if (!etapa?.peso_final_ativo) return null;
  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
      <p className="mb-2 text-sm font-semibold text-violet-900">Peso final</p>
      <label className="mb-1 block text-sm font-medium text-gray-700">
        Peso final <span className="text-red-500">*</span>
      </label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="decimal"
          value={valor ?? ''}
          onChange={(e) => setValor(e.target.value)}
          placeholder="Ex.: 12,50"
          className="flex-1 rounded-lg border px-3 py-2 text-sm"
        />
        <span className="text-sm text-gray-500">{unidade || 'kg'}</span>
      </div>
      <p className="mt-1 text-xs text-gray-500">Informe o peso ao concluir esta etapa do processo.</p>
    </div>
  );
}

/** Converte texto de peso final para número (ou null). */
export function parsePesoFinal(valor) {
  return paraNumero(valor);
}

/** Campo de seleção de embalagens permitidas na etapa. */
export function EmbalagensEtapaField({ etapa, selecionadas, setSelecionadas }) {
  const permitidas = etapa?.embalagens_permitidas || [];
  if (!permitidas.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="mb-2 text-sm font-semibold text-amber-900">Embalagens utilizadas *</p>
      <p className="mb-2 text-xs text-amber-800">Selecione uma ou mais entre as permitidas no produto.</p>
      <div className="space-y-1.5">
        {permitidas.map((emb) => {
          const checked = (selecionadas || []).some((s) => s.id === emb.id);
          return (
            <label key={emb.id} className="flex items-center gap-2 rounded border bg-white px-2 py-1.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  setSelecionadas((prev) => {
                    const list = prev || [];
                    if (list.some((s) => s.id === emb.id)) return list.filter((s) => s.id !== emb.id);
                    return [...list, { id: emb.id, nome: emb.nome || '', codigo: emb.codigo || '' }];
                  });
                }}
              />
              <span className="font-medium">{emb.nome || emb.id}</span>
              {emb.codigo ? <span className="text-xs text-gray-500">{emb.codigo}</span> : null}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** Formulário com os parâmetros parametrizados da etapa. */
export function ParametrosEtapaForm({ etapa, qualidadeVals, setQualidadeVals, producaoVals, setProducaoVals }) {
  const params = etapa?.parametros || [];
  const valids = etapa?.validacoes || [];
  if (params.length === 0 && valids.length === 0) {
    return <p className="text-sm text-gray-400">Esta etapa não tem parâmetros cadastrados.</p>;
  }
  return (
    <div className="space-y-4">
      {params.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="mb-2 text-sm font-semibold text-blue-900">Parâmetros de Qualidade</p>
          <div className="space-y-3">
            {params.map((p, i) => {
              const valor = qualidadeVals[p.nome] ?? '';
              const conf = conformeRange(p, valor);
              return (
                <div key={i}>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {p.nome} {p.obrigatorio && <span className="text-red-500">*</span>}
                  </label>
                  {(p.tipo === 'range' || !p.tipo || p.tipo === 'texto') && (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        inputMode={p.tipo === 'range' ? 'decimal' : 'text'}
                        value={valor}
                        onChange={(e) => setQualidadeVals((s) => ({ ...s, [p.nome]: e.target.value }))}
                        placeholder={p.tipo === 'range' ? `${p.min} – ${p.max}` : ''}
                        className="flex-1 rounded-lg border px-3 py-2 text-sm"
                      />
                      {p.unidade && <span className="text-sm text-gray-500">{p.unidade}</span>}
                      {p.tipo === 'range' && conf === true && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                      {p.tipo === 'range' && conf === false && <XCircle className="h-5 w-5 text-red-500" />}
                    </div>
                  )}
                  {p.tipo === 'boolean' && (
                    <select
                      value={valor}
                      onChange={(e) => setQualidadeVals((s) => ({ ...s, [p.nome]: e.target.value }))}
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                    >
                      <option value="">Selecione…</option>
                      <option value="sim">Sim</option>
                      <option value="nao">Não</option>
                    </select>
                  )}
                  {p.tipo === 'range' && (
                    <p className="mt-1 text-xs text-gray-500">
                      Faixa esperada: {p.min} – {p.max} {p.unidade}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {valids.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="mb-2 text-sm font-semibold text-amber-900">Parâmetros de Produção</p>
          <div className="space-y-3">
            {valids.map((v, i) => (
              <div key={i}>
                <label className="mb-1 block text-sm font-medium text-gray-700">{v.nome || `Parâmetro ${i + 1}`}</label>
                <input
                  type="text"
                  value={producaoVals[v.nome] ?? ''}
                  onChange={(e) => setProducaoVals((s) => ({ ...s, [v.nome]: e.target.value }))}
                  placeholder="Valor medido / registrado"
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Captura das fotos obrigatórias da etapa (câmera traseira / arquivo). */
export function FotosEtapaCapture({ etapa, valores, setValores, empresaId, setorId, titulo }) {
  const slots = etapa?.fotos_etapa || [];
  const [busy, setBusy] = useState(null);
  const [erroFoto, setErroFoto] = useState('');
  if (slots.length === 0) return null;

  const enviar = async (slotKey, file) => {
    if (!file) return;
    setBusy(slotKey);
    setErroFoto('');
    try {
      const url = await uploadApontamentoFoto(file, { empresaId, setorId });
      setValores((s) => ({ ...s, [slotKey]: url }));
    } catch (e) {
      setErroFoto(e?.message || 'Falha ao enviar a foto da etapa.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
      <p className="mb-2 flex items-center gap-1 text-sm font-semibold text-emerald-900">
        <Camera className="h-4 w-4" /> {titulo || 'Fotos da etapa'}
      </p>
      {erroFoto ? <p className="mb-2 text-xs text-red-700">{erroFoto}</p> : null}
      <div className="grid grid-cols-2 gap-2">
        {slots.map((slot, i) => {
          const key = String(slot.id || slot.nome || i);
          const url = valores[key];
          return (
            <label key={key} className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border bg-white p-2 text-center">
              {url ? (
                <img src={url} alt={slot.nome} className="h-16 w-full rounded object-cover" />
              ) : (
                <div className="flex h-16 w-full items-center justify-center text-gray-300">
                  {busy === key ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-6 w-6" />}
                </div>
              )}
              <span className="text-xs text-gray-600">{slot.nome || `Foto ${i + 1}`}</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => enviar(key, e.target.files?.[0])}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** Constrói o payload de fotos da etapa. */
export function construirFotosEtapaPayload(etapa, valores) {
  return (etapa?.fotos_etapa || [])
    .map((slot, i) => {
      const key = String(slot.id || slot.nome || i);
      const url = valores[key];
      return url ? { slot_id: key, nome: slot.nome || '', url } : null;
    })
    .filter(Boolean);
}

/** Retorna nomes das fotos ainda sem captura. */
export function fotosEtapaFaltando(etapa, valores = {}) {
  return (etapa?.fotos_etapa || [])
    .filter((slot, i) => {
      const key = String(slot.id || slot.nome || i);
      return !String(valores[key] || '').trim();
    })
    .map((slot, i) => slot.nome || `Foto ${i + 1}`);
}
